import { router } from "@inertiajs/react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import ProductLink from "@/components/productQuickView/ProductLink";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/Table";
import FlagCell, { type FlagChoice } from "./FlagCell";
import { warehouse_channel_product_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";

export interface ProductRow {
  id: string;
  product_external_id: string;
  name: string | null;
  source: "seed_from_sales" | "seed_from_inventory" | "manual";
  selling: boolean;
  gifting: boolean;
  selling_manual: boolean;
  gifting_manual: boolean;
}

const SOURCE_LABELS: Record<ProductRow["source"], string> = {
  seed_from_sales: "sales",
  seed_from_inventory: "inventory",
  manual: "manual",
};

type Flag = "selling" | "gifting";

interface Props {
  rows: ProductRow[];
  canManage: boolean;
}

// Selling/gifting product list for one warehouse × channel link.
export default function ProductGrid({ rows, canManage }: Props) {
  const market = useMarket();

  function remove(row: ProductRow) {
    router.delete(warehouse_channel_product_path(market.slug, row.id));
  }

  function setFlag(row: ProductRow, flag: Flag, choice: FlagChoice) {
    router.patch(
      warehouse_channel_product_path(market.slug, row.id),
      { warehouse_channel_product: { [flag]: choice } },
      { preserveScroll: true },
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-background">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="w-40">ID</TableHead>
            <TableHead className="w-28">Source</TableHead>
            <TableHead className="w-24">Selling</TableHead>
            <TableHead className="w-24">Gifting</TableHead>
            {canManage && <TableHead className="w-20" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="text-foreground">
                {row.name ? (
                  <ProductLink externalId={row.product_external_id}>{row.name}</ProductLink>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell>
                <span className="select-all text-muted-foreground">
                  {row.product_external_id}
                </span>
              </TableCell>
              <TableCell>
                <Badge variant="outline">
                  {SOURCE_LABELS[row.source] ?? row.source}
                </Badge>
              </TableCell>
              {(["selling", "gifting"] as const).map((flag) => (
                <TableCell key={flag}>
                  <FlagCell
                    value={row[flag]}
                    manual={row[`${flag}_manual`]}
                    canManage={canManage}
                    onSelect={(choice) => setFlag(row, flag, choice)}
                  />
                </TableCell>
              ))}
              {canManage && (
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => remove(row)}>
                    Remove
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={canManage ? 6 : 5}
                className="text-center text-muted-foreground py-8"
              >
                No products on this warehouse × channel yet. Seed or add one.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
